import { machineTypeCatalogEntrySchema } from "./machine-type-catalog.js";
import { appPoolRegionSchema } from "./app-pool.js";
import { z } from "zod";
//#region src/dp-satellites.ts
/**
* The satellite admin routes: a data plane admin enables a region for a data plane, picks the
* machine types the region's satellite cluster may run, and disables it again
* (2026-09-29-gpu-and-regional-app-pools, D-9, D-10, D-14; `00-index.md` §4).
*
* Two services serve the same three verbs. provisioning-svc owns the table
* `provisioning.dp_satellites` and serves the paths under `/v1/data-planes` (WS-5). site-admin-api
* proxies them under `/v1/admin` (WS-6), and the console calls those (WS-7). Both prefixes are
* reachable from outside the cluster: the CP gateway's `cp-httproute` sends every
* `/v1/data-planes` path to provisioning-svc. The control on each service is its own
* `@RequirePermission`: {@link DP_SATELLITES_READ_PERMISSION} on `GET`,
* {@link DP_SATELLITES_WRITE_PERMISSION} on `PUT` and `DELETE`. Both services bind the schemas
* below with `@Param({ schema })` and `@Body({ schema })` under the global
* `ZionStrictValidationPipe`, so an unknown key at any depth is a 400 before a handler runs.
*
* The verbs:
* - `GET` the satellites path: every satellite row of the data plane. provisioning-svc answers
*   `dpSatellitesResponseSchema`, the rows alone. site-admin-api answers
*   `dpSatellitesAdminResponseSchema`: each row with the phase and the observed node counts the
*   home operator last reported for its region, read from `release.dp_live_status.satellites`
*   (R-021).
* - `PUT` the satellite path: create or replace the row for `:region`, body
*   `putDpSatelliteRequestSchema`, answer `dpSatelliteSchema`. The server refuses a region absent
*   from `provisioning.machine_type_catalog` and canonicalises every machine type by `nodeType`
*   against that region's catalog, the way the enabled-machine-types PATCH does (DF-5).
* - `DELETE` the satellite path: remove the row and, through the XR patch, the satellite cluster.
*   It answers 409 with `dpSatelliteInUseErrorSchema` while any app-pool node group names the region
*   (D-10, DF-3).
*
* - `GET` the satellite job path: the latest job of one satellite, `dpSatelliteJobSchema`
*   (2026-10-24-aws-dp-identity-and-lifecycle, contract 3, D-25).
* - `GET` the satellite regions path: the regions and machine types a satellite may use on one
*   cloud, query `dpSatelliteRegionsQuerySchema`, answer `dpSatelliteRegionsResponseSchema`
*   (contract 3, D-26).
* - `GET` and `PUT` the satellite region policy path: the regions, per cloud, where the satellites
*   of one data plane may run. Body `putDpSatelliteRegionPolicyRequestSchema`, answer
*   `dpSatelliteRegionPolicyResponseSchema` (contract 4 of 2026-10-24-aws-dp-identity-and-lifecycle,
*   D-29, R-044).
*
* The data plane id comes from the path and the caller's identity from the JWT; neither route reads
* an org or a data plane from the body.
*
* Cross-cloud satellites (2026-10-24-aws-dp-identity-and-lifecycle, contract 2, D-17, D-21, D-23,
* D-25): a satellite runs on `gcp` or `aws`, for a home data plane on either cloud. The row view
* carries three optional fields, so a producer from before this change still answers a conforming
* body: the satellite's own `account`, the state of its `vpn` to the home network, and its latest
* `job` with one entry per step. No field here carries a VPN key, a credential or a bearer link
* (default 8 of that plan).
*/
/**
* The clouds a satellite may run on: `gcp` and `aws`, in that order (contract 2 of
* 2026-10-24-aws-dp-identity-and-lifecycle, D-17, D-23). Release 1 of the regional pools accepted
* `gcp` alone (D-14 of 2026-09-29-gpu-and-regional-app-pools); `aws` is the second member that ruling
* left room for. A PUT naming any other value fails `putDpSatelliteRequestSchema` and the pipe
* answers 400 (DF-3). Whether a given data plane may take a satellite on a given cloud is the PUT
* handler's check, never this list's.
*
* Declared here, in the lower of the two contract packages, because `@zion/sdk-cp-dp-contracts`
* imports `@zion/sdk-contracts` and not the reverse. The desired-state `SatelliteSpecSchema` reads
* the same list, so it widens with this one.
*/
const SATELLITE_PROVIDERS = ["gcp", "aws"];
/**
* The kinds of cloud account a satellite can run in: a GCP project or an AWS account. The order
* follows {@link SATELLITE_PROVIDERS}.
*/
const SATELLITE_ACCOUNT_KINDS = ["gcp-project", "aws-account"];
/**
* The account kind of each satellite cloud: `gcp` runs in a `gcp-project`, `aws` in an
* `aws-account`. The row view refuses an `account` whose kind is not the one its `provider` maps to.
* Frozen; a reader never writes it.
*/
const SATELLITE_ACCOUNT_KIND_BY_PROVIDER = Object.freeze({
	gcp: "gcp-project",
	aws: "aws-account"
});
/** provisioning-svc: every satellite of one data plane. The CP gateway routes the `/v1/data-planes`
*  prefix here, so the route's own `@RequirePermission` is the control that holds. */
const DP_SATELLITES_PATH = "/v1/data-planes/:id/satellites";
/** provisioning-svc: the satellite of one data plane in one region. */
const DP_SATELLITE_PATH = `${DP_SATELLITES_PATH}/:region`;
/** site-admin-api: the browser-reachable proxy of {@link DP_SATELLITES_PATH}. */
const DP_SATELLITES_ADMIN_PATH = "/v1/admin/data-planes/:id/satellites";
/** site-admin-api: the browser-reachable proxy of {@link DP_SATELLITE_PATH}. */
const DP_SATELLITE_ADMIN_PATH = `${DP_SATELLITES_ADMIN_PATH}/:region`;
/**
* provisioning-svc: the latest job of the satellite of one data plane in one region (contract 3,
* D-25). Path parameters `dpSatellitePathParamsSchema`; permission
* {@link DP_SATELLITES_READ_PERMISSION}. The answer is `dpSatelliteJobSchema`. A satellite with no
* job, and a region with no satellite, answer the service's 404.
*/
const DP_SATELLITE_JOB_PATH = `${DP_SATELLITE_PATH}/job`;
/** site-admin-api: the browser-reachable proxy of {@link DP_SATELLITE_JOB_PATH}, same answer. */
const DP_SATELLITE_JOB_ADMIN_PATH = `${DP_SATELLITE_ADMIN_PATH}/job`;
/**
* provisioning-svc: the regions and machine types a satellite may use on one cloud (contract 3,
* D-26). Query `dpSatelliteRegionsQuerySchema`; permission {@link DP_SATELLITES_READ_PERMISSION};
* answer `dpSatelliteRegionsResponseSchema`. The path holds no data plane id: the list is the
* cloud's catalog, the same for every data plane.
*
* The literal segment `satellite-regions` sits where `GET /v1/data-planes/:id` takes a uuid. Nest
* matches routes in declaration order, so the handler must register before that `:id` route, as
* `ProvisioningController.listRegions()` does for `regions`. Below it, the literal reaches the uuid
* check and the caller gets a 400.
*/
const DP_SATELLITE_REGIONS_PATH = "/v1/data-planes/satellite-regions";
/** site-admin-api: the browser-reachable proxy of {@link DP_SATELLITE_REGIONS_PATH}, same answer. */
const DP_SATELLITE_REGIONS_ADMIN_PATH = "/v1/admin/data-planes/satellite-regions";
/** The permission the `GET` verb requires on both services, the one every sibling data plane read
*  holds (`APP_POOL_ADMIN_SCOPE`, the machine-types read). */
const DP_SATELLITES_READ_PERMISSION = "cp:dp:read";
/** The permission `PUT` and `DELETE` require on both services. It is the one the enabled
*  machine-types PATCH requires on both services, and a satellite is that PATCH for another region
*  (DF-2, DF-5). `CpPermission` has no `cp:dp:write` member. */
const DP_SATELLITES_WRITE_PERMISSION = "cp:dp:update";
/** The `code` of the 409 a `DELETE` answers while a node group still names the region (D-10). */
const DP_SATELLITE_IN_USE_CODE = "SATELLITE_IN_USE";
/**
* Path parameters of the two satellites-collection routes. `id` is the data plane id, an RFC 9562
* UUID. A malformed id is a 400; an unknown well-formed id is the service's 404.
*/
/** The data plane id, lower-cased on parse: Postgres prints a `uuid` in lower case, and the answer's
*  invariant compares `dataPlaneId` as an exact string (R-068). `.toLowerCase()` is a zod 4 overwrite,
*  so `createOpenApiDocument()` still renders it as a uuid string; a `.transform()` throws there (R-069). */
const dpIdParamSchema = z.string().uuid().toLowerCase();
const dpSatellitesPathParamsSchema = z.object({ id: dpIdParamSchema }).strict();
/**
* Path parameters of the three single-satellite routes: the data plane id and the region in
* `APP_POOL_REGION_PATTERN`'s grammar (DF-1). The grammar is the one a pool's `region` uses, so a
* region an app can name is a region an admin can enable. Whether the region exists is the PUT
* handler's catalog check, never this schema's.
*/
const dpSatellitePathParamsSchema = z.object({
	id: dpIdParamSchema,
	region: appPoolRegionSchema
}).strict();
/**
* The `PUT` body: the satellite's cloud and the machine types its node groups may use. The region is
* the path's, and the body carries none, so the two can never disagree.
*
* `enabledMachineTypes` holds full catalog entries, the shape the enabled-machine-types PATCH sends
* (DF-5). At least one entry, because a satellite with no machine type can place nothing. The
* server replaces each entry with the region catalog's own entry for that `nodeType`, so a client's
* price or ceiling never reaches the table. PUT is replace: the body is the whole selection.
*
* The request carries the three facts of contract 2 of 2026-10-24-aws-dp-identity-and-lifecycle:
* `provider` and `enabledMachineTypes` here, and `region` in `dpSatellitePathParamsSchema`. The body
* names no account: a cross-cloud satellite gets a new account or project from the CP (D-21), and a
* same-cloud satellite runs in its home's.
*/
const putDpSatelliteRequestSchema = z.object({
	provider: z.enum(SATELLITE_PROVIDERS),
	enabledMachineTypes: z.array(machineTypeCatalogEntrySchema).min(1)
}).strict();
/**
* The cloud account a satellite runs in (contract 2, D-21): `{ kind: 'gcp-project', id }` with `id`
* a GCP project id, or `{ kind: 'aws-account', id }` with `id` a 12-digit AWS account id.
*
* Who writes it: provisioning-svc (2026-10-24-aws-dp-identity-and-lifecycle WS-9). A cross-cloud
* satellite names the account or project its own TenantCloudBase `tcb-<dpName>-sat-<region>`
* vended. A same-cloud satellite names its home's. Who reads it: site-admin-api, the console and
* `zion-ops`.
*
* A kind outside {@link SATELLITE_ACCOUNT_KINDS}, an `id` outside the kind's grammar, a missing key
* or an extra key fails with a `ZodError`. A conforming object parses to itself. `.strict()`. Pure.
*/
const dpSatelliteAccountSchema = z.discriminatedUnion("kind", [z.object({
	kind: z.literal("gcp-project"),
	id: z.string().regex(/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/, "a GCP project id")
}).strict(), z.object({
	kind: z.literal("aws-account"),
	id: z.string().regex(/^[0-9]{12}$/, "a 12-digit AWS account id")
}).strict()]);
/**
* The states of the private path from a satellite to its home network (contract 2, D-16, D-18):
*
* - `none`: the satellite has no VPN. A same-cloud satellite of a GCP home sits in the home VPC.
* - `pending`: the CP composed the VPN and its tunnels are not up yet.
* - `up`: the VPN carries the satellite to home NATS flow.
* - `down`: the VPN was up once, or its build ended, and it carries nothing now.
*/
const SATELLITE_VPN_STATES = [
	"none",
	"pending",
	"up",
	"down"
];
/**
* The steps of a satellite job, in the order a build runs them (contract 2, D-25): the account (the
* TenantCloudBase of a cross-cloud satellite), the network, the VPN, the cluster, and `ready`, the
* first status report of the satellite. A job lists only the steps it runs: a same-cloud satellite
* of a GCP home has no `account` step and no `vpn` step.
*/
const SATELLITE_JOB_STEP_NAMES = [
	"account",
	"network",
	"vpn",
	"cluster",
	"ready"
];
/**
* The states of a satellite job and of each of its steps (contract 2, D-25). One list serves both:
*
* - `pending`: not started.
* - `running`: started and not finished. A step that stays here names its cause in `reason`.
* - `done`: finished.
* - `failed`: stopped with an error that `reason` names.
*/
const SATELLITE_JOB_STATES = [
	"pending",
	"running",
	"done",
	"failed"
];
/** The longest `reason` a job step carries, in UTF-16 code units: the cap of the in-use message. */
const SATELLITE_JOB_REASON_MAX_LENGTH = 1024;
/** Raises the issue of a step whose `reason` disagrees with its state, at `['reason']`. */
function stepReasonIssues(step, ctx) {
	if (step.state === "failed" && step.reason === null) ctx.addIssue({
		code: "custom",
		path: ["reason"],
		message: "a failed step carries a reason"
	});
	if ((step.state === "pending" || step.state === "done") && step.reason !== null) ctx.addIssue({
		code: "custom",
		path: ["reason"],
		message: "only a running or failed step carries a reason"
	});
}
/**
* One step of a satellite job (contract 2, D-25).
*
* - `name`: one of {@link SATELLITE_JOB_STEP_NAMES}.
* - `state`: one of {@link SATELLITE_JOB_STATES}.
* - `reason`: text for a person, 1 to {@link SATELLITE_JOB_REASON_MAX_LENGTH} characters, or `null`.
*   A `failed` step must carry one. A `running` step may carry one, for example the cause of a
*   cluster that does not start. A `pending` or `done` step carries `null`. The producer never puts
*   a VPN key, a credential or a bearer link in it.
*
* A reason that disagrees with the state is an issue at `['reason']`. `.strict()`. Pure.
*/
const dpSatelliteJobStepSchema = z.object({
	name: z.enum(SATELLITE_JOB_STEP_NAMES),
	state: z.enum(SATELLITE_JOB_STATES),
	reason: z.string().min(1).max(SATELLITE_JOB_REASON_MAX_LENGTH).nullable()
}).strict().superRefine(stepReasonIssues);
/**
* Raises the issues of one job whose steps disagree with each other or with the job state:
* - a step name seen above it: at `['steps', i, 'name']`, "each step appears once";
* - a second `running` step: at `['steps', i, 'state']`, "one step runs at a time";
* - a job state the steps contradict: at `['state']`, one message per rule.
*/
function jobIssues(job, ctx) {
	const seen = /* @__PURE__ */ new Set();
	let running = 0;
	job.steps.forEach((step, i) => {
		if (seen.has(step.name)) ctx.addIssue({
			code: "custom",
			path: [
				"steps",
				i,
				"name"
			],
			message: "each step appears once"
		});
		seen.add(step.name);
		if (step.state === "running") {
			running += 1;
			if (running > 1) ctx.addIssue({
				code: "custom",
				path: [
					"steps",
					i,
					"state"
				],
				message: "one step runs at a time"
			});
		}
	});
	const states = job.steps.map((s) => s.state);
	const stateIssue = (message) => ctx.addIssue({
		code: "custom",
		path: ["state"],
		message
	});
	switch (job.state) {
		case "pending":
			if (states.some((s) => s !== "pending")) stateIssue("a pending job has every step pending");
			break;
		case "done":
			if (states.some((s) => s !== "done")) stateIssue("a done job has every step done");
			break;
		case "running":
			if (states.includes("failed")) stateIssue("a running job has no failed step");
			if (states.every((s) => s === "done")) stateIssue("a running job has a step not done");
			break;
		case "failed":
			if (!states.includes("failed")) stateIssue("a failed job has a failed step");
			if (states.includes("running")) stateIssue("a failed job has no running step");
	}
}
/**
* The latest job of one satellite: its build or its removal (contract 2, D-25). The answer of
* {@link DP_SATELLITE_JOB_PATH}, and the `job` of the row view.
*
* - `id`: the job id, an RFC 9562 UUID.
* - `state`: one of {@link SATELLITE_JOB_STATES}.
* - `steps`: 1 to 5 entries, each name at most once, in the order the job runs them.
*
* The schema holds these rules, so a producer that breaks one answers a body its own contract
* refuses:
* - A step name appears once. A repeat is an issue at its `name`.
* - At most one step is `running`. A second is an issue at its `state`.
* - A `pending` job has every step `pending`; a `done` job has every step `done`.
* - A `running` job has no `failed` step and at least one step that is not `done`.
* - A `failed` job has a `failed` step and no `running` step.
* - Each step's own `reason` rule of {@link dpSatelliteJobStepSchema}.
*
* Together the rules say: a step is `failed` exactly when the job is `failed`. `.strict()`. Pure.
*/
const dpSatelliteJobSchema = z.object({
	id: z.string().uuid(),
	state: z.enum(SATELLITE_JOB_STATES),
	steps: z.array(dpSatelliteJobStepSchema).min(1).max(SATELLITE_JOB_STEP_NAMES.length)
}).strict().superRefine(jobIssues);
/** Raises the issue of a row whose `account` kind is not the one its `provider` maps to, at
*  `['account', 'kind']`. An absent or `null` account raises nothing. */
function accountMatchesProvider(row, ctx) {
	if (row.account !== void 0 && row.account !== null && row.account.kind !== SATELLITE_ACCOUNT_KIND_BY_PROVIDER[row.provider]) ctx.addIssue({
		code: "custom",
		path: ["account", "kind"],
		message: "the account kind matches the provider"
	});
}
/** The fields of one satellite row, before the account rule. {@link dpSatelliteSchema} and
*  {@link dpSatelliteAdminSchema} both build on it, because zod 4 refuses `.extend()` on a refined
*  object. */
const dpSatelliteObject = z.object({
	dataPlaneId: z.string().uuid(),
	provider: z.enum(SATELLITE_PROVIDERS),
	region: appPoolRegionSchema,
	enabledMachineTypes: z.array(machineTypeCatalogEntrySchema).min(1),
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
	account: dpSatelliteAccountSchema.nullable().optional(),
	vpn: z.enum(SATELLITE_VPN_STATES).optional(),
	job: dpSatelliteJobSchema.nullable().optional()
}).strict();
/**
* One row of `provisioning.dp_satellites` as both services serve it: the answer to a `PUT` and one
* member of the `GET` list. `enabledMachineTypes` is the canonicalised selection, never empty.
* `createdAt` and `updatedAt` are ISO 8601 instants; a replacing `PUT` keeps `createdAt` and moves
* `updatedAt`.
*
* Three optional fields (contract 2 of 2026-10-24-aws-dp-identity-and-lifecycle, D-23). Absent
* means the producer predates them; it is never a state:
* - `account`: {@link dpSatelliteAccountSchema}, or `null` while the CP holds no account for the
*   satellite, for example before its TenantCloudBase is ready. A present account has the kind
*   that {@link SATELLITE_ACCOUNT_KIND_BY_PROVIDER} gives for `provider`; another kind is an issue
*   at `['account', 'kind']`, "the account kind matches the provider".
* - `vpn`: one of {@link SATELLITE_VPN_STATES}.
* - `job`: {@link dpSatelliteJobSchema}, the latest job of the satellite, or `null` when it has none.
*/
const dpSatelliteSchema = dpSatelliteObject.superRefine(accountMatchesProvider);
/** The issue message for a `GET` answer member whose `dataPlaneId` is not the answer's. */
const FOREIGN_DATA_PLANE_MESSAGE = "every satellite carries the answer's dataPlaneId";
/** The issue message for a `GET` answer member whose region sorts before the one above it. */
const REGION_ORDER_MESSAGE = "satellites ascend by region";
/** The issue message for a `GET` answer that names one region twice. */
const REGION_REPEAT_MESSAGE = "each region appears once";
/** Whether no region appears twice among the members of one `GET` answer. */
function regionsUnique(rows) {
	return new Set(rows.map((r) => r.region)).size === rows.length;
}
/**
* Raises the per-member issues of one `GET` answer: a member whose `dataPlaneId` is not the answer's,
* at `['satellites', i, 'dataPlaneId']`, and a member whose region sorts below the one above it, at
* `['satellites', i, 'region']`. A repeated region is not a descent. Both answer schemas call it, so
* the two answers hold one rule set.
*/
function memberIssues(answer, ctx) {
	answer.satellites.forEach((row, i) => {
		if (row.dataPlaneId !== answer.dataPlaneId) ctx.addIssue({
			code: "custom",
			path: [
				"satellites",
				i,
				"dataPlaneId"
			],
			message: FOREIGN_DATA_PLANE_MESSAGE
		});
		const above = answer.satellites[i - 1];
		if (above !== void 0 && row.region < above.region) ctx.addIssue({
			code: "custom",
			path: [
				"satellites",
				i,
				"region"
			],
			message: REGION_ORDER_MESSAGE
		});
	});
}
/**
* The `GET` answer provisioning-svc serves. `satellites` is `[]` for a data plane with no region
* enabled. The schema enforces three invariants, so a service that breaks one answers a body its own
* contract refuses:
* - Regions are unique within one answer (the table's primary key is `(data_plane_id, region)`).
*   A repeat is one issue at `['satellites']`, "each region appears once".
* - Every member carries the answer's `dataPlaneId`. A foreign member is an issue at
*   `['satellites', i, 'dataPlaneId']`.
* - Members ascend by `region` in code-unit order, so two reads of one state compare equal. A member
*   that sorts before the one above it is an issue at `['satellites', i, 'region']`. A repeated
*   region is not a descent, so a duplicate raises the uniqueness issue alone.
*/
const dpSatellitesResponseSchema = z.object({
	dataPlaneId: z.string().uuid(),
	satellites: z.array(dpSatelliteSchema).refine(regionsUnique, { message: REGION_REPEAT_MESSAGE })
}).strict().superRefine(memberIssues);
/**
* The phases of one satellite as the home operator reports it: `pending` until the home operator
* receives the satellite's first status message, `ready` while messages arrive, `stale` once the
* latest is older than 120 seconds (R-012). The home operator assigns the phase; the satellite
* never sends one.
*
* Declared here, in the lower of the two contract packages, for the reason `SATELLITE_PROVIDERS` is
* (R-006): `dpSatelliteObservedStatusSchema` needs the list, and this package cannot import
* `@zion/sdk-cp-dp-contracts`. `StatusReport.satellites[].phase` carries the same three values.
*/
const SATELLITE_PHASES = [
	"pending",
	"ready",
	"stale"
];
/** Whether the counts agree with the phase: all three absent for `pending`, all three present for
*  `ready` and `stale`. A partial set matches neither arm. */
function countsMatchPhase(status) {
	const present = [
		status.nodeGroupCount,
		status.nodeCount,
		status.readyNodeCount
	].filter((n) => n !== void 0).length;
	return status.phase === "pending" ? present === 0 : present === 3;
}
/**
* What the home operator last reported for one satellite, as site-admin-api joins it onto the row
* from `release.dp_live_status` (R-021). The console's Regions section renders it (WS-7).
*
* - `phase`: the entry's phase, one of {@link SATELLITE_PHASES}.
* - `receivedAt`: the ISO 8601 instant the CP received the status report the phase came from, the
*   row's `received_at`. A reader compares it with the clock to tell a current `ready` from a
*   report nobody has refreshed.
* - `nodeGroupCount`: the regional node groups the satellite observed, the length of the entry's
*   `nodeGroups`.
* - `nodeCount`, `readyNodeCount`: the sums of the entry's per-group `nodeCount` and
*   `readyNodeCount`. `readyNodeCount` never exceeds `nodeCount`.
*
* The three counts are absent exactly when `phase` is `pending` and present otherwise: absent means
* nothing was measured, and a zero would claim a measurement. Each is a whole number from 0 up.
* `.strict()`. A conforming object parses to itself; anything else is a `ZodError`. Pure.
*/
const dpSatelliteObservedStatusSchema = z.object({
	phase: z.enum(SATELLITE_PHASES),
	receivedAt: z.string().datetime(),
	nodeGroupCount: z.number().int().nonnegative().optional(),
	nodeCount: z.number().int().nonnegative().optional(),
	readyNodeCount: z.number().int().nonnegative().optional()
}).strict().refine(countsMatchPhase, {
	message: "the counts are absent for a pending satellite and present otherwise",
	path: ["phase"]
}).refine((s) => s.readyNodeCount === void 0 || s.nodeCount === void 0 || s.readyNodeCount <= s.nodeCount, {
	message: "readyNodeCount cannot exceed nodeCount",
	path: ["readyNodeCount"]
});
/**
* One member of site-admin-api's `GET` answer: the stored row of {@link dpSatelliteSchema} and
* `status`, the home operator's last report for the row's region. `status` is `null` when no status
* report names the region: the data plane has sent no report, its report carries no `satellites`,
* or the operator has not yet picked the new region up from its desired state. `null` is the
* answer "not reported", never a phase. The row's optional `account`, `vpn` and `job` and its
* account rule are the ones of {@link dpSatelliteSchema}. `.strict()`. Pure.
*/
const dpSatelliteAdminSchema = dpSatelliteObject.extend({ status: dpSatelliteObservedStatusSchema.nullable() }).strict().superRefine(accountMatchesProvider);
/**
* The `GET` answer site-admin-api serves on {@link DP_SATELLITES_ADMIN_PATH}: the rows of
* provisioning-svc's answer, in the same order, each with its `status` (R-021). It holds the three
* invariants of {@link dpSatellitesResponseSchema} with the same issue paths and messages: unique
* regions, one `dataPlaneId`, ascending regions. `satellites` is `[]` for a data plane with no
* region enabled. `.strict()`. Pure.
*/
const dpSatellitesAdminResponseSchema = z.object({
	dataPlaneId: z.string().uuid(),
	satellites: z.array(dpSatelliteAdminSchema).refine(regionsUnique, { message: REGION_REPEAT_MESSAGE })
}).strict().superRefine(memberIssues);
/**
* The body of the 409 a `DELETE` answers while `nodeGroupCount` app-pool node groups still name
* `region` (D-10). The satellite and its row are untouched. `message` is for a person and says to
* remove the regional pools first; a client keys on `code`. The console renders `message` as the
* refusal reason (WS-7).
*/
const dpSatelliteInUseErrorSchema = z.object({
	code: z.literal(DP_SATELLITE_IN_USE_CODE),
	message: z.string().min(1).max(1024),
	region: appPoolRegionSchema,
	nodeGroupCount: z.number().int().min(1)
}).strict();
/**
* The query of {@link DP_SATELLITE_REGIONS_PATH} (contract 3, D-26): `?provider=gcp` or
* `?provider=aws`. The parameter is required; a missing one, a value outside
* {@link SATELLITE_PROVIDERS} or an extra parameter is a 400 under the strict pipe. `.strict()`. Pure.
*/
const dpSatelliteRegionsQuerySchema = z.object({ provider: z.enum(SATELLITE_PROVIDERS) }).strict();
/**
* One region of the satellite regions answer: the region name in `APP_POOL_REGION_PATTERN`'s grammar
* and the catalog entries of the machine types a satellite there may enable, at least one. A region
* with no machine type can place nothing, so the answer leaves it out. `.strict()`. Pure.
*/
const dpSatelliteRegionSchema = z.object({
	region: appPoolRegionSchema,
	machineTypes: z.array(machineTypeCatalogEntrySchema).min(1)
}).strict();
/** Raises an issue at `['regions', i, 'region']` for each region that sorts below the one above it.
*  A repeated region is not a descent. */
function regionsAscend(answer, ctx) {
	answer.regions.forEach((row, i) => {
		const above = answer.regions[i - 1];
		if (above !== void 0 && row.region < above.region) ctx.addIssue({
			code: "custom",
			path: [
				"regions",
				i,
				"region"
			],
			message: "regions ascend by name"
		});
	});
}
/**
* The answer of {@link DP_SATELLITE_REGIONS_PATH}: the regions where a satellite may run on
* `provider`, from that cloud's machine-type catalog (D-26). `provider` echoes the query. `regions`
* is `[]` for a cloud whose catalog holds no region. The schema holds two invariants, with the issue
* paths and the uniqueness message of the satellites answers:
* - Each region appears once. A repeat is one issue at `['regions']`, "each region appears once".
* - Regions ascend by name in code-unit order. A region that sorts before the one above it is an
*   issue at `['regions', i, 'region']`, "regions ascend by name".
*
* The answer is the same for every data plane; whether a region suits one data plane (its home
* region, a region already enabled) is the PUT handler's check. `.strict()`. Pure.
*/
const dpSatelliteRegionsResponseSchema = z.object({
	provider: z.enum(SATELLITE_PROVIDERS),
	regions: z.array(dpSatelliteRegionSchema).refine(regionsUnique, { message: REGION_REPEAT_MESSAGE })
}).strict().superRefine(regionsAscend);
/**
* provisioning-svc: the satellite region policy of one data plane (contract 4 of
* 2026-10-24-aws-dp-identity-and-lifecycle, D-29, R-044). The regions, per cloud, where the
* satellites of the data plane may run. Path parameters `dpSatellitesPathParamsSchema`.
*
* - `GET` requires {@link DP_SATELLITES_READ_PERMISSION} and answers
*   `dpSatelliteRegionPolicyResponseSchema`. A data plane with no stored policy answers `null` for
*   both clouds. A malformed id is a 400; an unknown well-formed id is the service's 404.
* - `PUT` requires {@link DP_SATELLITES_WRITE_PERMISSION}. The body is
*   `putDpSatelliteRegionPolicyRequestSchema` and replaces the whole policy; the answer is the
*   `GET` answer after the write. provisioning-svc writes the audit action
*   `data_plane.satellite_regions_updated`. The service answers:
*   - 400 for a ticked region outside the satellite region list of its cloud. Only
*     provisioning-svc holds those lists, so this schema checks the grammar alone.
*   - 409 for a policy that unticks a region where the data plane holds a satellite on that cloud.
*     The message names each such region. The stored policy stays as it was.
*
* The policy also gates the satellite `PUT` on {@link DP_SATELLITE_PATH}. A `PUT` that creates a new
* satellite, or retries a failed build, in a region outside the effective list of its cloud
* answers 409. A replacing `PUT` of a stored satellite stays allowed, so no satellite that exists
* breaks. The home region stays refused whatever the policy says.
*/
const DP_SATELLITE_REGION_POLICY_PATH = "/v1/data-planes/:id/satellite-region-policy";
/** site-admin-api: the browser-reachable proxy of {@link DP_SATELLITE_REGION_POLICY_PATH}, same
*  verbs, bodies and answers. The proxy `PUT` writes no audit row of its own: provisioning-svc
*  writes it. */
const DP_SATELLITE_REGION_POLICY_ADMIN_PATH = "/v1/admin/data-planes/:id/satellite-region-policy";
/**
* The most regions one cloud's list of a satellite region policy holds: 64. The GCP satellite
* region list holds 42 regions and the AWS list 17 (D-28), so a policy can tick every region of
* either cloud. The bound keeps the body of a `PUT` finite.
*/
const SATELLITE_REGION_POLICY_MAX_REGIONS = 64;
/** Raises an issue at `[i]` for each region that equals one above it, "each region appears once". */
function regionListRepeats(list, ctx) {
	const seen = /* @__PURE__ */ new Set();
	list.forEach((region, i) => {
		if (seen.has(region)) ctx.addIssue({
			code: "custom",
			path: [i],
			message: REGION_REPEAT_MESSAGE
		});
		seen.add(region);
	});
}
/** Raises an issue at `[i]` for each region that sorts below the one above it, "regions ascend by
*  name". A repeated region is not a descent. */
function regionListAscends(list, ctx) {
	list.forEach((region, i) => {
		const above = list[i - 1];
		if (above !== void 0 && region < above) ctx.addIssue({
			code: "custom",
			path: [i],
			message: "regions ascend by name"
		});
	});
}
/** One cloud's list in a policy `PUT`: 0 to {@link SATELLITE_REGION_POLICY_MAX_REGIONS} distinct
*  region names, in any order. */
const policyRegionListSchema = z.array(appPoolRegionSchema).max(64).superRefine(regionListRepeats);
/** One cloud's list in a policy answer: the request list's rules, and the regions ascend by name. */
const policyRegionListAnswerSchema = policyRegionListSchema.superRefine(regionListAscends);
/**
* The body of a satellite region policy `PUT` (contract 4, D-29, R-044): the regions, per cloud,
* where the satellites of the data plane may run. Callers: the site-admin-api proxy, the console's
* policy card, the MCP tool and `zion-ops`.
*
* Both keys are required, so a body always states the whole policy:
* - `gcp`, `aws`: the ticked regions of that cloud, or `null`. `null` means every region of that
*   cloud's satellite region list, now and as the list grows; it is the state of a data plane with
*   no stored policy. An empty array means no satellite on that cloud.
*
* Each list holds 0 to {@link SATELLITE_REGION_POLICY_MAX_REGIONS} names, each in
* `APP_POOL_REGION_PATTERN`'s grammar, in any order. These fail with a `ZodError`:
* - A missing key, an extra key at the top, and a value that is neither an array nor `null`.
* - A name outside the grammar: the issue path is `[cloud, i]`.
* - A name that equals one above it: the issue path is `[cloud, i]`, "each region appears once".
* - A list longer than the bound.
*
* Whether each name is in its cloud's satellite region list is the service's 400, never this
* schema's. A conforming body parses to itself, in the same order. `.strict()`. Pure.
*/
const putDpSatelliteRegionPolicyRequestSchema = z.object({
	gcp: policyRegionListSchema.nullable(),
	aws: policyRegionListSchema.nullable()
}).strict();
/** Raises an issue at `['effective', cloud, i]` for each effective region outside a stored list of
*  that cloud. A `null` stored list admits every region, so it raises nothing. */
function effectiveWithinPolicy(answer, ctx) {
	for (const cloud of SATELLITE_PROVIDERS) {
		const ticked = answer[cloud];
		if (ticked === null) continue;
		const admitted = new Set(ticked);
		answer.effective[cloud].forEach((region, i) => {
			if (!admitted.has(region)) ctx.addIssue({
				code: "custom",
				path: [
					"effective",
					cloud,
					i
				],
				message: "every effective region is a ticked region"
			});
		});
	}
}
/**
* The answer of the satellite region policy `GET` and `PUT` (contract 4, D-29, R-044).
*
* - `gcp`, `aws`: the stored policy of that cloud, or `null` when the data plane stores none for
*   it. A data plane with no stored policy answers `null` for both.
* - `effective.gcp`, `effective.aws`: the regions where a new satellite on that cloud may run
*   now. For a `null` stored list it is that cloud's whole satellite region list. For a stored
*   list it is the stored names that are still in that cloud's list.
*
* The four lists each hold the rules of the request lists, and each ascends by name in code-unit
* order, so two reads of one state compare equal. The schema refuses, each with a `ZodError`:
* - A repeated region: at `[cloud, i]` or `['effective', cloud, i]`, "each region appears once".
* - A region that sorts below the one above it: at the same paths, "regions ascend by name". A
*   repeated region is not a descent, so a duplicate raises the uniqueness issue alone.
* - An effective region outside a stored list of the same cloud: at `['effective', cloud, i]`,
*   "every effective region is a ticked region".
* - A missing key, an extra key at either level, and a `null` effective list.
*
* The effective lists keep the home region and the regions that hold a satellite: the setup flow
* removes those. `.strict()`. A conforming object parses to itself. Pure.
*/
const dpSatelliteRegionPolicyResponseSchema = z.object({
	gcp: policyRegionListAnswerSchema.nullable(),
	aws: policyRegionListAnswerSchema.nullable(),
	effective: z.object({
		gcp: policyRegionListAnswerSchema,
		aws: policyRegionListAnswerSchema
	}).strict()
}).strict().superRefine(effectiveWithinPolicy);
//#endregion
export { DP_SATELLITES_ADMIN_PATH, DP_SATELLITES_PATH, DP_SATELLITES_READ_PERMISSION, DP_SATELLITES_WRITE_PERMISSION, DP_SATELLITE_ADMIN_PATH, DP_SATELLITE_IN_USE_CODE, DP_SATELLITE_JOB_ADMIN_PATH, DP_SATELLITE_JOB_PATH, DP_SATELLITE_PATH, DP_SATELLITE_REGIONS_ADMIN_PATH, DP_SATELLITE_REGIONS_PATH, DP_SATELLITE_REGION_POLICY_ADMIN_PATH, DP_SATELLITE_REGION_POLICY_PATH, SATELLITE_ACCOUNT_KINDS, SATELLITE_ACCOUNT_KIND_BY_PROVIDER, SATELLITE_JOB_REASON_MAX_LENGTH, SATELLITE_JOB_STATES, SATELLITE_JOB_STEP_NAMES, SATELLITE_PHASES, SATELLITE_PROVIDERS, SATELLITE_REGION_POLICY_MAX_REGIONS, SATELLITE_VPN_STATES, dpSatelliteAccountSchema, dpSatelliteAdminSchema, dpSatelliteInUseErrorSchema, dpSatelliteJobSchema, dpSatelliteJobStepSchema, dpSatelliteObservedStatusSchema, dpSatellitePathParamsSchema, dpSatelliteRegionPolicyResponseSchema, dpSatelliteRegionSchema, dpSatelliteRegionsQuerySchema, dpSatelliteRegionsResponseSchema, dpSatelliteSchema, dpSatellitesAdminResponseSchema, dpSatellitesPathParamsSchema, dpSatellitesResponseSchema, putDpSatelliteRegionPolicyRequestSchema, putDpSatelliteRequestSchema };

//# sourceMappingURL=dp-satellites.js.map